import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  notificationSettingsService, 
  NotificationSettings, 
  BannerPreferences,
  NotificationTypeSettings,
  ScheduledNotificationSettings,
  NotificationFrequencySettings
} from '../services/NotificationSettingsService';

export interface UseNotificationSettingsReturn {
  // Current state
  settings: NotificationSettings | null;
  bannerPreferences: BannerPreferences | null;
  isLoading: boolean;
  error: string | null;

  // Settings management
  updateSettings: (updates: Partial<NotificationSettings>) => Promise<void>;
  updateBannerPreferences: (preferences: Partial<BannerPreferences>) => Promise<void>;
  
  // Convenience methods for common operations
  toggleDeliveryMethod: (method: 'bell' | 'email' | 'banner') => Promise<void>;
  toggleCategory: (category: string) => Promise<void>;
  toggleNotificationType: (type: keyof NotificationTypeSettings) => Promise<void>;
  updateScheduledSettings: (updates: Partial<ScheduledNotificationSettings>) => Promise<void>;
  updateNotificationFrequency: (type: keyof NotificationFrequencySettings, frequency: string) => Promise<void>;
  
  // Utility methods
  isDeliveryMethodEnabled: (method: 'bell' | 'email' | 'banner') => boolean;
  isCategoryEnabled: (category: string) => boolean;
  isNotificationTypeEnabled: (type: keyof NotificationTypeSettings) => boolean;
  getNotificationFrequency: (type: keyof NotificationFrequencySettings) => string;
  isInQuietHours: () => Promise<boolean>;
  
  // Cache management
  refreshSettings: () => Promise<void>;
  clearCache: () => void;
}

export function useNotificationSettings(): UseNotificationSettingsReturn {
  const { user } = useAuth();
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [bannerPreferences, setBannerPreferences] = useState<BannerPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load settings when user changes
  useEffect(() => {
    if (user?.id) {
      loadSettings();
    } else {
      setSettings(null);
      setBannerPreferences(null);
      setIsLoading(false);
    }
  }, [user?.id]);

  const loadSettings = useCallback(async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      setError(null);
      
      const userSettings = await notificationSettingsService.getNotificationSettings(user.id);
      setSettings(userSettings);
      
      // Banner preferences are loaded separately for now
      // In a full implementation, you might want to load them together
      setBannerPreferences({ enabled: true });
    } catch (err) {
      console.error('Error loading notification settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load notification settings');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  const updateSettings = useCallback(async (updates: Partial<NotificationSettings>) => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    try {
      setError(null);
      const updatedSettings = await notificationSettingsService.updateNotificationSettings(
        user.id, 
        updates
      );
      setSettings(updatedSettings);
    } catch (err) {
      console.error('Error updating notification settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to update notification settings');
      throw err;
    }
  }, [user?.id]);

  const updateBannerPreferences = useCallback(async (preferences: Partial<BannerPreferences>) => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    try {
      setError(null);
      const updatedPreferences = await notificationSettingsService.updateBannerPreferences(
        user.id, 
        preferences
      );
      setBannerPreferences(updatedPreferences);
    } catch (err) {
      console.error('Error updating banner preferences:', err);
      setError(err instanceof Error ? err.message : 'Failed to update banner preferences');
      throw err;
    }
  }, [user?.id]);

  const toggleDeliveryMethod = useCallback(async (method: 'bell' | 'email' | 'banner') => {
    if (!settings) return;
    
    // When toggling bell (in-app), also toggle banner to keep them in sync
    if (method === 'bell') {
      await updateSettings({
        bell: !settings.bell,
        banner: !settings.bell  // Set banner to same state as bell
      });
    } else if (method === 'banner') {
      // If somehow banner is toggled directly, sync with bell
      await updateSettings({
        bell: !settings.banner,
        banner: !settings.banner
      });
    } else {
      await updateSettings({
        [method]: !settings[method]
      });
    }
  }, [settings, updateSettings]);

  const toggleCategory = useCallback(async (category: string) => {
    if (!settings) return;
    
    await updateSettings({
      categories: {
        ...settings.categories,
        [category]: !settings.categories[category]
      }
    });
  }, [settings, updateSettings]);

  const toggleNotificationType = useCallback(async (type: keyof NotificationTypeSettings) => {
    if (!settings) return;
    
    await updateSettings({
      scheduled_notifications: {
        ...settings.scheduled_notifications,
        notification_types: {
          ...settings.scheduled_notifications.notification_types,
          [type]: !settings.scheduled_notifications.notification_types[type]
        }
      }
    });
  }, [settings, updateSettings]);

  const updateScheduledSettings = useCallback(async (updates: Partial<ScheduledNotificationSettings>) => {
    if (!settings) return;
    
    await updateSettings({
      scheduled_notifications: {
        ...settings.scheduled_notifications,
        ...updates
      }
    });
  }, [settings, updateSettings]);

  const updateNotificationFrequency = useCallback(async (type: keyof NotificationFrequencySettings, frequency: string) => {
    if (!settings) return;
    
    await updateSettings({
      scheduled_notifications: {
        ...settings.scheduled_notifications,
        notification_frequencies: {
          ...settings.scheduled_notifications.notification_frequencies,
          [type]: frequency
        }
      }
    });
  }, [settings, updateSettings]);

  const isDeliveryMethodEnabled = useCallback((method: 'bell' | 'email' | 'banner'): boolean => {
    // Banner is controlled by bell (in-app notifications)
    if (method === 'banner') {
      return settings?.bell ?? false;
    }
    return settings?.[method] ?? false;
  }, [settings]);

  const isCategoryEnabled = useCallback((category: string): boolean => {
    return settings?.categories[category] ?? false;
  }, [settings]);

  const isNotificationTypeEnabled = useCallback((type: keyof NotificationTypeSettings): boolean => {
    return settings?.scheduled_notifications.notification_types[type] ?? false;
  }, [settings]);

  const getNotificationFrequency = useCallback((type: keyof NotificationFrequencySettings): string => {
    return settings?.scheduled_notifications.notification_frequencies[type] ?? '';
  }, [settings]);

  const isInQuietHours = useCallback(async (): Promise<boolean> => {
    if (!user?.id) return false;
    
    try {
      return await notificationSettingsService.isInQuietHours(user.id);
    } catch (err) {
      console.error('Error checking quiet hours:', err);
      return false;
    }
  }, [user?.id]);

  const refreshSettings = useCallback(async () => {
    await loadSettings();
  }, [loadSettings]);

  const clearCache = useCallback(() => {
    if (user?.id) {
      notificationSettingsService.clearCache(user.id);
    }
  }, [user?.id]);

  return {
    // Current state
    settings,
    bannerPreferences,
    isLoading,
    error,

    // Settings management
    updateSettings,
    updateBannerPreferences,

    // Convenience methods
    toggleDeliveryMethod,
    toggleCategory,
    toggleNotificationType,
    updateScheduledSettings,
    updateNotificationFrequency,

    // Utility methods
    isDeliveryMethodEnabled,
    isCategoryEnabled,
    isNotificationTypeEnabled,
    getNotificationFrequency,
    isInQuietHours,

    // Cache management
    refreshSettings,
    clearCache
  };
} 